import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { resolveImageUrl } from "@/components/ImageInput";
import { Trophy, DownloadSimple } from "@phosphor-icons/react";

export default function ReportsTab({ auction }) {
  const [summary, setSummary] = useState(null);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, t, p] = await Promise.all([
        api.get(`/auctions/${auction.id}/reports/summary`),
        api.get(`/auctions/${auction.id}/teams`),
        api.get(`/auctions/${auction.id}/players`),
      ]);
      setSummary(s.data); setTeams(t.data); setPlayers(p.data);
    })().catch(() => {});
  }, [auction.id]);

  const download = async (kind) => {
    try {
      const res = await api.get(`/auctions/${auction.id}/reports/export?kind=${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const nameMap = { sold: "sold_players.csv", unsold: "unsold_players.csv", all: "auction_report.csv", teamwise: "sold_players_teamwise.xlsx" };
      a.download = nameMap[kind] || "report.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export failed", e);
    }
  };

  if (!summary) return <div className="label-mono py-16 text-center">Loading reports…</div>;
  const sold = players.filter((p) => p.status === "sold");
  const unsold = players.filter((p) => p.status === "unsold");

  return (
    <div className="space-y-8" data-testid="reports-tab">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="label-mono">REPORTS · export any tab as CSV / Excel</div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => download("sold")}
            className="btn-ghost text-xs flex items-center gap-2"
            data-testid="export-sold-btn"
          >
            <DownloadSimple size={14} /> Sold (CSV)
          </button>
          <button
            type="button"
            onClick={() => download("unsold")}
            className="btn-ghost text-xs flex items-center gap-2"
            data-testid="export-unsold-btn"
          >
            <DownloadSimple size={14} /> Unsold (CSV)
          </button>
          <button
            type="button"
            onClick={() => download("all")}
            className="btn-ghost text-xs flex items-center gap-2"
            data-testid="export-all-btn"
          >
            <DownloadSimple size={14} /> All (CSV)
          </button>
          <button
            type="button"
            onClick={() => download("teamwise")}
            className="btn-primary text-xs flex items-center gap-2"
            title="Excel with one sheet per team listing that team's sold players (incl. shirt & bottom sizes)"
            data-testid="export-teamwise-btn"
          >
            <DownloadSimple size={14} /> Sold Team-wise (XLSX)
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Players", summary.total_players],
          ["Teams", summary.total_teams],
          ["Sold", summary.sold],
          ["Unsold", summary.unsold],
          ["Spent", summary.total_spent],
        ].map(([l, v]) => (
          <div key={l} className="bg-white border border-gray-200 rounded p-4">
            <div className="label-mono">{l}</div>
            <div className="stat-number">{v}</div>
          </div>
        ))}
      </div>

      <section>
        <h3 className="font-display uppercase text-3xl mb-4">Team-wise Squads</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {teams.map((t) => {
            const tp = sold.filter((p) => p.sold_to === t.id);
            return (
              <div key={t.id} className="bg-white border border-gray-200 rounded p-4" data-testid={`report-team-${t.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  {t.logo_url ? (
                    <img
                      src={resolveImageUrl(t.logo_url)}
                      alt=""
                      className="w-10 h-10 rounded object-cover border"
                      style={{ borderColor: `${t.color}55` }}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-10 h-10 grid place-items-center rounded" style={{ background: `${t.color}22`, border: `1px solid ${t.color}55` }}>
                      <Trophy weight="fill" size={18} style={{ color: t.color }} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl uppercase leading-none truncate">{t.name}</div>
                    <div className="label-mono">{t.short_name} · Spent {t.budget - t.remaining_budget} / {t.budget}</div>
                  </div>
                  <div className="font-display text-2xl">{tp.length}</div>
                </div>
                {tp.length === 0 ? (
                  <div className="text-gray-500 text-sm">No players.</div>
                ) : (
                  <div className="space-y-1">
                    {tp.map((p) => (
                      <div key={p.id} className="flex justify-between text-sm border-b border-gray-200 py-1">
                        <div><div className="font-semibold">{p.name}</div><div className="label-mono">{p.role}</div></div>
                        <div className="text-emerald-600 font-mono">{p.sold_price} pts</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="font-display uppercase text-3xl mb-4">Unsold Players ({unsold.length})</h3>
        {unsold.length === 0 ? (
          <div className="border border-dashed border-gray-200 rounded p-6 text-gray-500 text-center">No unsold players.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {unsold.map((p) => (
              <div key={p.id} className="bg-white border border-gray-200 rounded p-3">
                <div className="label-mono">{p.role} · Cat {p.category}</div>
                <div className="font-semibold">{p.name}</div>
                <div className="text-xs text-gray-500">Base {p.base_price}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
